import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import { respEnum, OpenMatchStatusEnum, OpenMatchTypeEnum } from "../../constants/appConstant";
import toast from "react-hot-toast";
import AdvanceTable from "../../components/advanceTable";

const OpenMatches = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isPast, setIsPast] = useState(false);
  const [filters, setFilters] = useState({
    clubId: null,
    sportId: null,
    cat_id: null,
    fromDate: null,
    tillDate: null,
  });

  useEffect(() => {
    getMatches();
  }, [isPast]);

  const getMatches = async (pageNumber = 1, searchTerm = "", recordsToDisplay = 10) => {
    setLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetOpenMatches",
        {
          isPast: isPast,
          pageNumber: pageNumber,
          recordsToDisplay: recordsToDisplay,
          status: OpenMatchStatusEnum.Pending,
          type: OpenMatchTypeEnum.Open,
          q: searchTerm,
          ...filters,
        },
        "POST",
        false
      );

      if (response?.status === respEnum.Success && response?.data) {
        const responseData = response.data[0] || {};
        const matchesArray = responseData.data || [];
        setMatches(matchesArray);
        setTotalRecords(responseData.totalRecords || 0);
      } else {
        toast.error(response?.message || "Failed to load matches");
        setMatches([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching matches:", error);
      toast.error("An error occurred while fetching matches");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinMatch = async (matchId) => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "JoinOpenMatch",
        null,
        "POST",
        false,
        { openMatchId: matchId }
      );

      if (response?.status === respEnum.Success) {
        toast.success("Successfully joined the match!");
        getMatches();
      } else {
        toast.error(response?.message || "Failed to join match");
      }
    } catch (error) {
      console.error("Error joining match:", error);
      toast.error("An error occurred");
    }
  };

  const columns = [
    { title: "ID", type: "number", isVisible: false, key: "id" },
    {
      title: "Club",
      type: "custom",
      isVisible: true,
      key: "clubName",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.clubLogo && (
            <img
              src={row.clubLogo}
              alt={row.clubName}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <span className="font-medium">{row.clubName}</span>
        </div>
      ),
    },
    {
      title: "Sport",
      type: "text",
      isVisible: true,
      key: "sportName",
    },
    {
      title: "Date & Time",
      type: "custom",
      isVisible: true,
      key: "start_datetime",
      render: (row) => (
        <div>
          <div className="font-medium">
            {new Date(row.start_datetime).toLocaleDateString()}
          </div>
          <div className="text-sm text-gray-500">
            {new Date(row.start_datetime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      ),
    },
    {
      title: "Players",
      type: "custom",
      isVisible: true,
      key: "playerCount",
      render: (row) => (
        <div className="flex items-center gap-2">
          <Icon icon="heroicons:users" className="w-5 h-5 text-blue-600" />
          <span className="font-semibold">{row.playerCount}/4</span>
        </div>
      ),
    },
    {
      title: "Creator",
      type: "custom",
      isVisible: true,
      key: "creatorName",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.creatorImage && (
            <img
              src={row.creatorImage}
              alt={row.creatorName}
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          <span>{row.creatorName}</span>
        </div>
      ),
    },
    {
      title: "Charges",
      type: "text",
      isVisible: true,
      key: "court_charges",
      render: (row) => <span className="font-semibold">Rs. {row.court_charges}</span>,
    },
    {
      title: "Status",
      type: "custom",
      isVisible: true,
      key: "status",
      render: (row) => {
        const statusMap = {
          [OpenMatchStatusEnum.Pending]: { label: "Pending", class: "bg-yellow-100 text-yellow-800" },
          [OpenMatchStatusEnum.Completed]: { label: "Completed", class: "bg-green-100 text-green-800" },
          [OpenMatchStatusEnum.Cancelled]: { label: "Cancelled", class: "bg-red-100 text-red-800" },
        };
        const status = statusMap[row.status] || statusMap[OpenMatchStatusEnum.Pending];
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.class}`}>
            {status.label}
          </span>
        );
      },
    },
    {
      title: "Actions",
      type: "custom",
      isVisible: true,
      key: "actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/app/openmatches/details?id=${row.id}`)}
          >
            <Icon icon="heroicons:eye" className="w-4 h-4" />
          </Button>
          {!isPast && row.playerCount < 4 && (
            <Button
              size="sm"
              onClick={() => handleJoinMatch(row.id)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Icon icon="heroicons:plus" className="w-4 h-4 mr-1" />
              Join
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Icon icon="heroicons:fire" className="w-6 h-6 text-orange-600" />
            Open Matches
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant={!isPast ? "default" : "outline"}
              onClick={() => setIsPast(false)}
            >
              <Icon icon="heroicons:clock" className="w-4 h-4 mr-2" />
              Upcoming
            </Button>
            <Button
              variant={isPast ? "default" : "outline"}
              onClick={() => setIsPast(true)}
            >
              <Icon icon="heroicons:archive-box" className="w-4 h-4 mr-2" />
              Past
            </Button>
            <Button
              onClick={() => navigate("/app/openmatches/create")}
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              <Icon icon="heroicons:plus" className="w-4 h-4 mr-2" />
              Create Match
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AdvanceTable
            _thead={columns}
            _tbody={matches}
            _totalRecords={totalRecords}
            fetchData={getMatches}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default OpenMatches;

